// ProfileView.jsx
import React, { useEffect, useState, useRef} from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import "../styles/profileview.css";
import "../styles/floatingForm.css";



const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

const ALLOWED_DOCUMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
];


/* ------------------ Helpers ------------------ */

const normalizeVendorData = (data) => {
  const cleaned = {};
  for (const key in data) cleaned[key] = data[key] ?? "";
  cleaned.email_id = cleaned.emailid || "";
  cleaned.mobile_no = cleaned.mobileno || "";
  return cleaned;
};


const resolveImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${api.defaults.baseURL}${path}`;
};

/* ------------------ Component ------------------ */

const ProfileView = () => {
  const [vendor, setVendor] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [faqs, setFaqs] = useState([]);

  const [panFile, setPanFile] = useState(null);
  const [panPreview, setPanPreview] = useState(null);

  const [gstFile, setGstFile] = useState(null);
  const [gstPreview, setGstPreview] = useState(null);

  const [documents, setDocuments] = useState([]);

  const revokeUrlsRef = useRef([]);

    const navigate = useNavigate();

  const handleCancel = () => {
    navigate("/");
  };

  /* ------------------ Fetch Vendor ------------------ */

  const fetchVendor = async () => {
    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) return;

    const res = await api.get(`/vendor/get/${vendorId}`);
    const safeData = normalizeVendorData(res.data?.data || {});
    console.log(safeData)
    setVendor(safeData);
    setFormData(safeData);

    setLogoPreview(resolveImageUrl(safeData.logo_image_path));
    setBannerPreview(resolveImageUrl(safeData.banner_image_path));
    setPanPreview(resolveImageUrl(safeData.pan_file));
    setGstPreview(resolveImageUrl(safeData.gst_document_file));
  };

  

  /* ------------------ Fetch FAQs (SHOW PREVIOUS) ------------------ */

  const fetchFaqs = async () => {
    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) return;

    const res = await api.get(`/vendor/getfaq/${vendorId}`);
    const list = res.data?.data || [];

    setFaqs(
      list.map((f) => ({
        question: f.faq_que || "",
        answer: f.faq_ans || "",
      }))
    );
  };
  const fetchDocuments = async () => {
    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) return;

    try {
      const res = await api.get(`/api/vendor/getdocuments/${vendorId}`);

      const list = res.data?.data || [];

      setDocuments(
        list.map((doc) => ({
          id: doc.id,
          name: doc.doc_name,
          file: null,
          preview: doc.doc_file.toLowerCase().endsWith(".pdf")
            ? null
            : resolveImageUrl(doc.doc_file),
          existingFile: resolveImageUrl(doc.doc_file),
          isPdf: doc.doc_file.toLowerCase().endsWith(".pdf"),
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchVendor();
    fetchFaqs();
    fetchDocuments();

    return () =>
      revokeUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  /* ------------------ Common ------------------ */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((p) => ({ ...p, [name]: "" }));
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleReadOnly = (fieldName, message) => (e) => {
    e.preventDefault();
    toast.info(message);
  };

  const showAlert = (msg) => {
    toast.success(msg);
  };

  /* ------------------ Image ------------------ */

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type)) return;

    const url = URL.createObjectURL(file);
    revokeUrlsRef.current.push(url);
    setLogoFile(file);
    setLogoPreview(url);
  };

  const onBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type)) return;

    const url = URL.createObjectURL(file);
    revokeUrlsRef.current.push(url);
    setBannerFile(file);
    setBannerPreview(url);
  };

  const onPanChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      toast.error("Only PDF, PNG, JPG and JPEG files are allowed.");
      return;
    }

    setPanFile(file);

    if (file.type === "application/pdf") {
      setPanPreview(null);
      return;
    }

    const url = URL.createObjectURL(file);
    revokeUrlsRef.current.push(url);
    setPanPreview(url);
  };

  const onGstChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      toast.error("Only PDF, PNG, JPG and JPEG files are allowed.");
      return;
    }

    setGstFile(file);

    if (file.type === "application/pdf") {
      setGstPreview(null);
      return;
    }

    const url = URL.createObjectURL(file);
    revokeUrlsRef.current.push(url);
    setGstPreview(url);
  };

  /* ------------------ FAQ ------------------ */
  
  const addFaq = () => {
    setFaqs((p) => [...p, { question: "", answer: "" }]);
  };

  const removeFaq = (index) => {
    setFaqs((p) => p.filter((_, i) => i !== index));
  };

  const updateFaq = (index, field, value) => {
    setFaqs((p) =>
      p.map((f, i) => (i === index ? { ...f, [field]: value } : f))
  );
};

  /* ------------------ More Docs ------------------ */

  const addDocument = () => {
    setDocuments((prev) => [
      ...prev,
      {
        name: "",
        file: null,
        preview: null,
      },
    ]);
  };

  const removeDocument = (index) => {
    setDocuments((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const updateDocumentName = (index, value) => {
    setDocuments((prev) =>
      prev.map((doc, i) =>
        i === index
          ? { ...doc, name: value }
          : doc
      )
    );
  };

  const handleDocDelete = async (id) => {
    try {
      const res = await api.get(`/api/vendor/deletedocumentbyid/${id}`);

      if (res.data.status === "success") {
        toast.success("Document deleted successfully.");

        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      } else {
        toast.error(res.data.message || "Failed to delete document.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while deleting document.");
    }
  };

  const updateDocumentFile = (index, file) => {
    if (!file) return;

    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      toast.error("Only PDF, PNG, JPG and JPEG files are allowed.");
      return;
    }

    let preview = null;

    if (file.type !== "application/pdf") {
      preview = URL.createObjectURL(file);
      revokeUrlsRef.current.push(preview);
    }

    setDocuments((prev) =>
      prev.map((doc, i) =>
        i === index
          ? {
              ...doc,
              file,
              preview,
            }
          : doc
      )
    );
  };

  /* ------------------ Submit (UPDATE + ADD) ------------------ */

  const handleUpdate = async () => {
    const fd = new FormData();

    Object.entries(formData).forEach(([k, v]) => {
      if (v !== null && v !== undefined) fd.append(k, v);
    });

    // ✅ BACKEND EXPECTS ARRAYS
    faqs
      .filter((f) => f.question.trim() && f.answer.trim())
      .forEach((f) => {
        fd.append("vfaqq[]", f.question);
        fd.append("vfaqans[]", f.answer);
      });

    if (logoFile) fd.append("logo_image_path", logoFile);
    if (bannerFile) fd.append("banner_image_path", bannerFile);
    if (panFile) {
      fd.append("pan_file", panFile);
    }
    if (gstFile) {
      fd.append("gst_document_file", gstFile);
    }

    documents
    .filter((doc) => doc.name && doc.file)
    .forEach((doc) => {
      fd.append("doc_name[]", doc.name);
      fd.append("doc_file[]", doc.file);
    });

    for (let pair of fd.entries()) {
      console.log(pair[0], pair[1]);
    }


    await api.post(`/vendor/update/${vendor.id}`, fd);
    showAlert("Profile updated successfully!");
    await fetchDocuments();
    await fetchFaqs();
  };

  if (!vendor) return <div className="profile-container">Loading...</div>;

  /* ------------------ UI (UPDATED) ------------------ */

  return (
        <div className="profile-container">
      {/* {showPopup && <div className="custom-popup">{popupMessage}</div>} */}
    <div className="floating-form-container1 ">
      <div className="heading">
        <h2>{vendor.name} Profile</h2>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
        <div className="form-container">
        {/* SECTION 1: Basic Info */}
        <div className="section-1">
          {/* Vendor Name */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="name">Service Provider Name</label>
            <input
              type="text"
              id="name"
              name="name"
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              value={formData.name || ""}
              onChange={handleChange}
              placeholder="Enter service provider name"
            />
            {errors.name && (
              <b>
                <span className="text-danger small">{errors.name}</span>
              </b>
            )}
          </div>

          {/* Vendor Email (readonly) */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="email">Service Provider Email</label>
            <input
              type="email"
              id="email"
              name="emailid"
              value={vendor.emailid || ""}
              readOnly
              className="form-control"
              onKeyDown={handleReadOnly(
                "emailid",
                "Email cannot be edited"
              )}
              onPaste={handleReadOnly("emailid", "Email cannot be edited")}
              onMouseDown={handleReadOnly("emailid", "Email cannot be edited")}
              onContextMenu={handleReadOnly("emailid", "Email cannot be edited")}
            />
            {errors.emailid && (
              <b>
                <span className="text-danger small">{errors.emailid}</span>
              </b>
            )}
          </div>

          {/* Vendor Mobile (readonly) */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="mobile">Service Provider Mobile</label>
            <input
              type="text"
              id="mobile"
              name="mobileno"
              value={vendor.mobileno || ""}
              readOnly
              className="form-control"
              onKeyDown={handleReadOnly(
                "mobileno",
                "Mobile number cannot be edited"
              )}
              onPaste={handleReadOnly(
                "mobileno",
                "Mobile number cannot be edited"
              )}
              onMouseDown={handleReadOnly(
                "mobileno",
                "Mobile number cannot be edited"
              )}
              onContextMenu={handleReadOnly(
                "mobileno",
                "Mobile number cannot be edited"
              )}
            />
            {errors.mobileno && (
              <b>
                <span className="text-danger small">{errors.mobileno}</span>
              </b>
            )}
          </div>
        </div>

        <hr className="form-rule" />

        {/* SECTION 2: GST & Company Details */}
        <div className="section-1">
          {/* GSTIN */}
          <div className="input-container-floating-form wid-25">
            <label htmlFor="gstin">GSTIN</label>
            <input
              type="text"
              id="gstin"
              name="gstin"
              className={`form-control ${errors.gstin ? "is-invalid" : ""}`}
              value={formData.gstin || ""}
              onChange={handleChange}
              placeholder="Enter GSTIN"
            />
            {errors.gstin && (
              <b>
                <span className="text-danger small">{errors.gstin}</span>
              </b>
            )}
          </div>

          {/* GST Document */}
          <div className="input-container-floating-form wid-25">
            <label>GST Document</label>

            <div className="file-select">
              <div className="file-select-button">
                Choose File
              </div>

              <div className="file-select-name">
                {gstFile?.name ||
                  (formData.gst_document_file
                    ? "Current GST Document"
                    : "No file chosen...")}
              </div>

              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={onGstChange}
              />
            </div>

            {gstPreview && (
              <div style={{ marginTop: 10 }}>
                <img
                  src={gstPreview}
                  alt="GST"
                  style={{
                    width: 100,
                    borderRadius: 5,
                  }}
                />
              </div>
            )}

            {gstFile?.type === "application/pdf" && (
              <div style={{ marginTop: 10 }}>
                📄 {gstFile.name}
              </div>
            )}
          </div>

          {/* Company PAN */}
          <div className="input-container-floating-form wid-25">
            <label htmlFor="pan">Company PAN</label>
            <input
              type="text"
              id="pan"
              name="comp_pan_no"
              className={`form-control ${
                errors.comp_pan_no ? "is-invalid" : ""
              }`}
              value={formData.comp_pan_no || ""}
              onChange={handleChange}
              placeholder="Enter Company PAN"
            />
            {errors.comp_pan_no && (
              <b>
                <span className="text-danger small">{errors.comp_pan_no}</span>
              </b>
            )}
          </div>

          <div className="input-container-floating-form wid-25">
            <label>PAN Document</label>

            <div className="file-select">
              <div className="file-select-button">
                Choose File
              </div>

              <div className="file-select-name">
                {panFile?.name ||
                  (formData.pan_file
                    ? "Current PAN Document"
                    : "No file chosen...")}
              </div>

              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={onPanChange}
              />
            </div>

            {panPreview && (
              <div style={{ marginTop: 10 }}>
                <img
                  src={panPreview}
                  alt="PAN"
                  style={{
                    width: 100,
                    borderRadius: 5,
                  }}
                />
              </div>
            )}

            {panFile?.type === "application/pdf" && (
              <div
                style={{
                  marginTop: 10,
                  color: "#666",
                }}
              >
                📄 {panFile.name}
              </div>
            )}
          </div>

          {/* Year */}
          <div className="input-container-floating-form wid-25">
            <label htmlFor="year">Year in Business</label>
            <input
              type="text"
              id="year"
              name="year"
              className={`form-control ${errors.year ? "is-invalid" : ""}`}
              value={formData.year || ""}
              onChange={handleChange}
              placeholder="Enter year"
            />
            {errors.year && (
              <b>
                <span className="text-danger small">{errors.year}</span>
              </b>
            )}
          </div>
        </div>

        <hr className="form-rule" />

        {/* SECTION 3: Director/Owner Info */}
        <div className="section-1">
          {/* Director Name */}
          <div className="input-container-floating-form wid-30">
            <label htmlFor="dname">Director/Owner Name</label>
            <input
              type="text"
              id="dname"
              name="comp_directer"
              className={`form-control ${
                errors.comp_directer ? "is-invalid" : ""
              }`}
              value={formData.comp_directer || ""}
              onChange={handleChange}
              placeholder="Enter director/owner name"
            />
            {errors.comp_directer && (
              <b>
                <span className="text-danger small">
                  {errors.comp_directer}
                </span>
              </b>
            )}
          </div>

          {/* Director Email */}
          <div className="input-container-floating-form wid-30">
            <label htmlFor="demail">Director/Owner Email</label>
            <input
              type="email"
              id="demail"
              name="comp_directer_emailid"
              className={`form-control ${
                errors.comp_directer_emailid ? "is-invalid" : ""
              }`}
              value={formData.comp_directer_emailid || ""}
              onChange={handleChange}
              placeholder="Enter director/owner email"
            />
            {errors.comp_directer_emailid && (
              <b>
                <span className="text-danger small">
                  {errors.comp_directer_emailid}
                </span>
              </b>
            )}
          </div>

          {/* Director Mobile */}
          <div className="input-container-floating-form wid-30">
            <label htmlFor="dmobile">Director/Owner Mobile</label>
            <input
              type="text"
              id="dmobile"
              name="comp_directer_mobile"
              className={`form-control ${
                errors.comp_directer_mobile ? "is-invalid" : ""
              }`}
              value={formData.comp_directer_mobile || ""}
              onChange={handleChange}
              onInput={(e) =>
                (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
              }
              maxLength="10"
              placeholder="Enter 10-digit mobile number"
            />
            {errors.comp_directer_mobile && (
              <b>
                <span className="text-danger small">
                  {errors.comp_directer_mobile}
                </span>
              </b>
            )}
          </div>
        </div>

        <hr className="form-rule" />

        {/* SECTION 4: Address & Location */}
        <div className="section-1">
          {/* Address */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              className={`form-control ${errors.address ? "is-invalid" : ""}`}
              value={formData.address || ""}
              onChange={handleChange}
              placeholder="Enter address"
            />
            {errors.address && (
              <b>
                <span className="text-danger small">{errors.address}</span>
              </b>
            )}
          </div>

          {/* City */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="cityname"
              className={`form-control ${errors.cityname ? "is-invalid" : ""}`}
              value={formData.cityname || ""}
              onChange={handleChange}
              placeholder="Enter city"
            />
            {errors.cityname && (
              <b>
                <span className="text-danger small">{errors.cityname}</span>
              </b>
            )}
          </div>

          {/* State */}
          <div className="input-container-floating-form wid-30">
            <label htmlFor="state">State</label>
            <input
              type="text"
              id="state"
              name="statename"
              className={`form-control ${
                errors.statename ? "is-invalid" : ""
              }`}
              value={formData.statename || ""}
              onChange={handleChange}
              placeholder="Enter state"
            />
            {errors.statename && (
              <b>
                <span className="text-danger small">{errors.statename}</span>
              </b>
            )}
          </div>

          {/* Pin Code */}
          <div className="input-container-floating-form wid-30">
            <label htmlFor="pincode">Pin Code</label>
            <input
              type="text"
              id="pincode"
              name="pincode"
              className={`form-control ${errors.pincode ? "is-invalid" : ""}`}
              value={formData.pincode || ""}
              onChange={handleChange}
              onInput={(e) =>
                (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
              }
              maxLength="6"
              placeholder="Enter 6-digit pin code"
            />
            {errors.pincode && (
              <b>
                <span className="text-danger small">{errors.pincode}</span>
              </b>
            )}
          </div>

          {/* Created Date (readonly) */}
          <div className="input-container-floating-form wid-30">
            <label htmlFor="created">Created Date</label>
            <input
              type="text"
              id="created"
              value={formData.created_date || ""}
              readOnly
              className="form-control"
            />
          </div>
        </div>

        <hr className="form-rule" />

        {/* SECTION 5: About */}
        <div className="section-1">
          <div className="input-container-floating-form wid-100">
            <label htmlFor="about">About</label>
            <textarea
              id="about"
              name="about"
              className={`form-control ${errors.about ? "is-invalid" : ""}`}
              rows="4"
              value={formData.about || ""}
              onChange={handleChange}
              placeholder="Enter about information"
            />
            {errors.about && (
              <b>
                <span className="text-danger small">{errors.about}</span>
              </b>
            )}
          </div>
        </div>

        <hr className="form-rule" />

        {/* SECTION 6: Image Upload */}
        <div className="section-1">
          <h2 className="wid-100">Upload Images</h2>

          {/* Logo Image */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="logo">
              Logo Image (Max 180×100) — PNG/JPEG only
            </label>
            <div className="file-select">
              <div className="file-select-button">Choose File</div>
              <div className="file-select-name">
                {logoFile?.name || "No file chosen..."}
              </div>
              <input
                type="file"
                id="logo"
                accept="image/png,image/jpeg"
                onChange={onLogoChange}
              />
            </div>
            {errors.logoImageFile && (
              <b>
                <span className="text-danger small">
                  {errors.logoImageFile}
                </span>
              </b>
            )}
            <div className="mt-2">
              {logoPreview && (
                <img
                  style={{ maxWidth: "100px", maxHeight: "100px" }}
                  src={logoPreview}
                  alt="Logo"
                />
              )}
            </div>
          </div>

          {/* Banner Image */}
          <div className="input-container-floating-form wid-50">
            <label htmlFor="banner">
              Banner Image (Max 1521×300) — PNG/JPEG only
            </label>
            <div className="file-select">
              <div className="file-select-button">Choose File</div>
              <div className="file-select-name">
                {bannerFile?.name || "No file chosen..."}
              </div>
              <input
                type="file"
                id="banner"
                accept="image/png,image/jpeg"
                onChange={onBannerChange}
              />
            </div>
            {errors.bannerImageFile && (
              <b>
                <span className="text-danger small">
                  {errors.bannerImageFile}
                </span>
              </b>
            )}
            <div className="mt-2">
              {bannerPreview && (
                <img
                  style={{ maxWidth: "100px", maxHeight: "100px" }}
                  src={bannerPreview}
                  alt="Banner"
                />
              )}
            </div>
          </div>
        </div>

        <hr className="form-rule" />
        {/* SECTION 7: FAQs */}
        <div className="section-1">
          <h2 className="wid-100">FAQs</h2>

          {faqs.map((faq, index) => (
            <div key={index} className="wid-100">
              <div className="input-container-floating-form">
                <label htmlFor={`question-${index}`} className="font-weight-bold">Question</label>
                <input
                  type="text"
                  id={`question-${index}`}
                  className="form-control"
                  value={faq.question}
                  onChange={(e) => updateFaq(index, "question", e.target.value)}
                  placeholder="Enter question"
                />
              </div>

              <div className="input-container-floating-form">
                <label htmlFor={`answer-${index}`} className="mt-2 font-weight-bold">Answer</label>
                <textarea
                  id={`answer-${index}`}
                  rows="3"
                  className="form-control"
                  value={faq.answer}
                  onChange={(e) => updateFaq(index, "answer", e.target.value)}
                  placeholder="Enter answer"
                />
              </div>

              {/* <button
                type="button"
                className="btn btn-sm btn-danger"
                style={{ marginTop: "8px", marginBottom: "10px" }}
                onClick={() => removeFaq(index)}
              >
                Remove
              </button> */}
              <hr className="form-rule" />
            </div>
          ))}

          <button
            type="button"
            className="btn"
            style={{
              marginTop: "8px",
              backgroundColor: "#BC6C25",
              color: "#fff",
              fontWeight: 700,
            }}
            onClick={addFaq}
          >
            + Add FAQ
          </button>
        </div>


          {/* More Docs */}
          <hr className="form-rule" />

          <div className="section-1">
            <h2 className="wid-100">
              Extra Documents
            </h2>

            {documents.map((doc, index) => (
              <div key={index} className="wid-100"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: "space-between"
                }}
              >

                <div className="input-container-floating-form wid-30">
                  <label>Document Name</label>

                  <input
                    type="text"
                    value={doc.name}
                    onChange={(e) =>
                      updateDocumentName(index, e.target.value)
                    }
                  />
                </div>

                <div className="input-container-floating-form wid-30">
                  <label>Document File</label>

                  <div className="file-select">

                    <div className="file-select-button">
                      Choose File
                    </div>

                    <div className="file-select-name">
                      {doc.file?.name ||
                        (doc.existingFile
                          ? "Current Document"
                          : "No file chosen...")}
                    </div>

                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) =>
                        updateDocumentFile(
                          index,
                          e.target.files[0]
                        )
                      }
                    />
                  </div>

                  {doc.isPdf ? (
                    <div style={{ marginTop: 10 }}>
                      📄 Existing PDF
                    </div>
                  ) : (
                    doc.preview && (
                      <img
                        src={doc.preview}
                        alt={doc.name}
                        style={{ width: 100, marginTop: 10 }}
                      />
                    )
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-danger"
                  style={{
                    marginTop: "40px"
                  }}
                  onClick={() => {
                    if (doc.id) {
                      handleDocDelete(doc.id);
                    } else {
                      removeDocument(index);
                    }
                  }}
                >
                  Remove
                </button>

                <hr className="form-rule"/>
              </div>
            ))}

            <button
              type="button"
              className="btn"
              style={{
                background:"#BC6C25",
                color:"#fff"
              }}
              onClick={addDocument}
            >
              + Add Document
            </button>
          </div>

        </div>
                {/* Form Buttons */}
        <div className="form-btn-container">
          <button type="reset" style={{backgroundColor:""}} onClick={handleCancel}>Cancel</button>
          <button type="submit">Update Profile</button>
        </div>
      </form>
    </div>
    </div>
  );
};

export default ProfileView;
